<template>
  <v-dialog v-model="dialog" max-width="1000" persistent scrollable>
    <v-card class="decontare-form-card">
      <!-- Header -->
      <v-card-title class="form-header">
        <span class="text-h6 font-weight-bold">{{ isEdit ? 'Editează Formular de Decontare' : copy ? 'Copie a formularului nr. ' + editData.id : 'Creează Formular de Decontare' }}</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="form-body">
        <!-- ═══════════════════════════════════════ -->
        <!-- SECTION 1: Informații generale          -->
        <!-- ═══════════════════════════════════════ -->
        <div class="section-label">
          <v-icon small color="#2d8659" class="mr-1">mdi-information-outline</v-icon>
          Informații generale
        </div>

        <v-card flat class="section-card mb-6">
          <v-row>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="form.department"
                :items="departments"
                label="Departament *"
                outlined
                dense
                prepend-inner-icon="mdi-domain"
                :rules="[v => !!v || 'Selectați departamentul']"
                placeholder="Căutați departamentul..."
                clearable
                auto-select-first
                :hint="departmentHint"
                persistent-hint
                @change="departmentHint = ''"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model.number="form.threshold_value"
                label="Valoare prag (MDL)"
                type="number"
                outlined
                dense
                prepend-inner-icon="mdi-cash"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <!-- placeholder for symmetry -->
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="form.notes"
                label="Note"
                outlined
                dense
                rows="2"
                auto-grow
                prepend-inner-icon="mdi-note-text-outline"
                placeholder="Observații sau detalii suplimentare..."
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card>

        <!-- ═══════════════════════════════════════ -->
        <!-- SECTION 2: Articole                     -->
        <!-- ═══════════════════════════════════════ -->
        <div class="section-label d-flex align-center">
          <v-icon small color="#2d8659" class="mr-1">mdi-format-list-bulleted</v-icon>
          Articole
          <v-spacer></v-spacer>
          <v-btn small color="#2d8659" dark elevation="1" @click="addItem">
            <v-icon left small>mdi-plus</v-icon>
            Adaugă articol
          </v-btn>
        </div>

        <div
          v-for="(item, idx) in form.items"
          :key="idx"
          class="item-card mb-5"
        >
          <!-- Item header bar -->
          <div class="item-header">
            <div class="item-number">{{ idx + 1 }}</div>
            <span class="font-weight-bold">Articol {{ idx + 1 }}</span>
            <v-spacer></v-spacer>
            <v-chip
              v-if="item.autoFilled"
              x-small
              color="#2d8659"
              outlined
              class="mr-2"
              title="Câmpurile goale au fost completate din ultimul articol cu această denumire"
            >Completat automat</v-chip>
            <v-chip
              v-if="isAboveThreshold(item)"
              x-small
              color="red lighten-1"
              text-color="white"
              class="mr-2"
            >Peste prag</v-chip>
            <v-btn
              small
              text
              color="#2d8659"
              class="mr-1"
              @click="duplicateItem(idx)"
              title="Adaugă un articol identic (fără cod și nr. inventar)"
            >
              <v-icon left small>mdi-content-copy</v-icon>
              Duplică
            </v-btn>
            <v-btn
              icon
              x-small
              @click="removeItem(idx)"
              v-if="form.items.length > 1"
              title="Șterge articolul"
            >
              <v-icon small color="red lighten-1">mdi-close-circle</v-icon>
            </v-btn>
          </div>

          <div class="item-body">
            <!-- Row 1: Category + Code + Name + Persoana -->
            <v-row>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="item.category_id"
                  :items="categories"
                  item-text="denumire"
                  item-value="id"
                  label="Tip Categorie *"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="item.codul"
                  label="Codul"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-combobox
                  v-model="item.denumire"
                  :items="articleNames"
                  label="Denumire *"
                  outlined
                  dense
                  hide-no-data
                  @change="onDenumireChange(item)"
                ></v-combobox>
              </v-col>
              <v-col cols="12" sm="3">
                <v-combobox
                  v-model="item.persoana"
                  :items="persoanaNames"
                  label="Persoana"
                  outlined
                  dense
                  hide-no-data
                  prepend-inner-icon="mdi-account"
                  @change="onPersoanaChange(item)"
                ></v-combobox>
              </v-col>
            </v-row>

            <!-- Row 2: Quantity + Price + Anul fabricației -->
            <v-row>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model.number="item.cantitatea"
                  label="Cantitatea *"
                  type="number"
                  min="1"
                  outlined
                  dense
                  @input="onCantitateaChange(item)"
                ></v-text-field>
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model.number="item.pret"
                  label="Preț unitar (MDL) *"
                  type="number"
                  outlined
                  dense
                  prepend-inner-icon="mdi-currency-usd"
                ></v-text-field>
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model.number="item.anul_fabricatie"
                  label="Anul fabricației"
                  type="number"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar"
                  placeholder="ex. 2018"
                ></v-text-field>
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model="item.data_punerii"
                  label="Data punerii în funcț."
                  type="date"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar-check"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Row 3: Durata funcționare utilă (ani → luni auto) -->
            <v-row>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model.number="item.anul_folosirii"
                  label="Durata funcț. utilă (ani)"
                  type="number"
                  min="0"
                  outlined
                  dense
                  prepend-inner-icon="mdi-timer-outline"
                  @input="onAnulChange(item)"
                ></v-text-field>
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model.number="item.lunii"
                  label="Luni (auto)"
                  type="number"
                  outlined
                  dense
                  readonly
                  background-color="#f5f5f5"
                  prepend-inner-icon="mdi-calendar-month"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Defect description sub-section -->
            <div class="subsection">
              <div class="subsection-header">
                <v-icon small color="red darken-1" class="mr-1">mdi-alert-circle-outline</v-icon>
                <span class="font-weight-medium">Descrierea defectului</span>
              </div>
              <v-row dense>
                <v-col cols="12" sm="5">
                  <v-select
                    :value="null"
                    :items="templateOptions"
                    label="Selectați un șablon"
                    outlined
                    dense
                    hide-details
                    prepend-inner-icon="mdi-file-document-edit-outline"
                    @input="applyTemplate($event, item)"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="item.defect_description"
                    label="Descrierea defectului"
                    outlined
                    rows="4"
                    auto-grow
                    hide-details
                    placeholder="Descrieți problema..."
                    class="mt-2"
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>

            <!-- Inventory Numbers sub-section (only when price > threshold) -->
            <div v-if="isAboveThreshold(item)" class="subsection threshold-section">
              <div class="subsection-header">
                <v-icon small color="orange darken-2" class="mr-1">mdi-barcode</v-icon>
                <span class="font-weight-medium">
                  Nr. Inventar
                  <span class="grey--text text--darken-1">- obligatoriu (preț depășește pragul)</span>
                </span>
              </div>
              <v-row dense>
                <v-col
                  cols="12" sm="4"
                  v-for="(inv, iIdx) in item.inventoryNumbers"
                  :key="'inv-' + iIdx"
                >
                  <v-text-field
                    v-model="item.inventoryNumbers[iIdx]"
                    :label="'Nr. Inventar ' + (iIdx + 1)"
                    outlined
                    dense
                    hide-details
                    prepend-inner-icon="mdi-pound"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Footer actions -->
      <v-divider></v-divider>
      <v-card-actions class="form-footer">
        <div class="summary-text" v-if="form.items.length > 0">
          {{ form.items.length }} articol{{ form.items.length > 1 ? 'e' : '' }}
          &middot;
          Total: <strong>{{ grandTotal.toFixed(2) }} MDL</strong>
        </div>
        <v-spacer></v-spacer>
        <v-btn text @click="close" class="mr-2">Anulare</v-btn>
        <v-btn
          color="#2d8659"
          dark
          @click="submitForm"
          :loading="saving"
          elevation="1"
        >
          <v-icon left small>mdi-content-save</v-icon>
          Salvează
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import * as equipmentApi from "../equipments/api";
import { mapGetters } from "vuex";

export default {
  name: "DecontareForm",
  props: {
    editData: { type: Object, default: null },
    // editData is only the starting point: save creates a new form ("Copiază formularul")
    copy: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: true,
      saving: false,
      categories: [],
      departments: [],
      templates: [],
      persoane: [],
      departmentHint: "",
      articles: [],
      form: {
        department: "",
        threshold_value: 12000,
        notes: "",
        items: [],
      },
    };
  },
  methods: {
    createEmptyItem() {
      return {
        category_id: null,
        codul: "",
        denumire: "",
        cantitatea: 1,
        pret: null,
        anul_folosirii: 0,
        lunii: 0,
        anul_fabricatie: null,
        data_punerii: null,
        defect_description: "",
        persoana: "",
        inventoryNumbers: [""],
        autoFilled: false,
      };
    },
    addItem() {
      const item = this.createEmptyItem();
      // items of one form usually belong to the same person
      const last = this.form.items[this.form.items.length - 1];
      if (last) item.persoana = last.persoana;
      this.form.items.push(item);
    },
    // same article again (e.g. 8 identical laptops): only the code and the inventory numbers differ
    duplicateItem(index) {
      const src = this.form.items[index];
      this.form.items.splice(index + 1, 0, {
        ...src,
        codul: "",
        autoFilled: false,
        inventoryNumbers: src.inventoryNumbers.map(() => ""),
      });
    },
    // the department follows the person (their latest form, else their account) while none is chosen
    onPersoanaChange(item) {
      if (this.form.department) return;
      const name = (item.persoana || "").replace(/\s+/g, " ").trim().toLowerCase();
      const person = this.persoane.find(p => p.name.toLowerCase() === name);
      if (person && person.department && this.departments.includes(person.department)) {
        this.form.department = person.department;
        this.departmentHint = `Completat după persoana ${person.name}; verificați`;
      }
    },
    // a name written before refills the empty fields from its latest item (never the code)
    onDenumireChange(item) {
      const name = (item.denumire || "").replace(/\s+/g, " ").trim().toLowerCase();
      const article = this.articles.find(a => a.denumire.replace(/\s+/g, " ").trim().toLowerCase() === name);
      if (!article) return;
      let filled = false;
      const fill = (field, value, isEmpty) => {
        if (isEmpty(item[field]) && value != null && value !== "") {
          item[field] = value;
          filled = true;
        }
      };
      const blank = v => v == null || v === "";
      fill("category_id", article.category_id, blank);
      fill("pret", article.pret != null ? Number(article.pret) : null, blank);
      fill("anul_fabricatie", article.anul_fabricatie, blank);
      fill("data_punerii", article.data_punerii ? String(article.data_punerii).substring(0, 10) : null, blank);
      fill("anul_folosirii", article.anul_folosirii || null, v => !v);
      if (filled) item.lunii = (item.anul_folosirii || 0) * 12;
      fill("defect_description", article.defect_description, v => !v || !v.trim());
      item.autoFilled = filled;
    },
    removeItem(index) {
      this.form.items.splice(index, 1);
    },
    isAboveThreshold(item) {
      return Number(item.pret || 0) > Number(this.form.threshold_value || 12000);
    },
    applyTemplate(templateText, item) {
      if (!templateText) return;
      // Replace parametric placeholders
      let text = templateText;
      text = text.replace(/\{denumirea\}/g, item.denumire || '___');
      text = text.replace(/\{categoria\}/g, this.getCategoryName(item.category_id) || '___');
      text = text.replace(/\{departament\}/g, this.form.department || '___');
      // Append to existing text instead of replacing
      if (item.defect_description && item.defect_description.trim()) {
        item.defect_description = item.defect_description.trimEnd() + '\n' + text;
      } else {
        item.defect_description = text;
      }
    },
    getCategoryName(categoryId) {
      if (!categoryId) return "";
      const cat = this.categories.find((c) => c.id === categoryId);
      return cat ? cat.denumire : "";
    },
    onCantitateaChange(item) {
      const qty = Math.max(1, item.cantitatea || 1);
      item.cantitatea = qty;
      while (item.inventoryNumbers.length < qty) {
        item.inventoryNumbers.push("");
      }
      while (item.inventoryNumbers.length > qty) {
        item.inventoryNumbers.pop();
      }
    },
    onAnulChange(item) {
      item.lunii = (item.anul_folosirii || 0) * 12;
    },
    validate() {
      if (!this.form.department) return "Selectați departamentul";

      for (let i = 0; i < this.form.items.length; i++) {
        const item = this.form.items[i];
        if (!item.category_id) return `Articolul ${i + 1}: Selectați categoria`;
        if (!item.denumire || !item.denumire.trim()) return `Articolul ${i + 1}: Introduceți denumirea`;
        if (item.cantitatea < 1) return `Articolul ${i + 1}: Cantitatea trebuie să fie >= 1`;
        if (!item.pret && item.pret !== 0) return `Articolul ${i + 1}: Introduceți prețul`;

        // When price > threshold: inventory numbers + anul_folosirii are required
        if (this.isAboveThreshold(item)) {
          if (!item.anul_folosirii || item.anul_folosirii < 1) {
            return `Articolul ${i + 1}: Ani folosirii este obligatoriu (preț depășește pragul)`;
          }
          for (let j = 0; j < item.inventoryNumbers.length; j++) {
            if (!item.inventoryNumbers[j] || !item.inventoryNumbers[j].trim()) {
              return `Articolul ${i + 1}: Completați Nr. Inventar ${j + 1} (obligatoriu când preț > prag)`;
            }
          }
        }
      }
      return null;
    },
    async submitForm() {
      const error = this.validate();
      if (error) {
        this.$emit("error", error);
        return;
      }

      this.saving = true;
      try {
        const postData = {
          department: this.form.department,
          threshold_value: this.form.threshold_value,
          notes: this.form.notes,
          created_by: this.getUser.userId,
          items: this.form.items.map((item) => ({
            category_id: item.category_id,
            codul: item.codul || null,
            denumire: item.denumire,
            cantitatea: item.cantitatea,
            pret: item.pret,
            anul_folosirii: item.anul_folosirii,
            lunii: item.lunii,
            anul_fabricatie: item.anul_fabricatie || null,
            data_punerii: item.data_punerii || null,
            defect_description: item.defect_description || null,
            persoana: (item.persoana || "").trim() || null,
            // Only send inventory numbers when above threshold
            inventoryNumbers: this.isAboveThreshold(item) ? item.inventoryNumbers : [],
          })),
        };

        let saved;
        if (this.isEdit) {
          saved = await api.updateFormFull(this.editData.id, postData);
        } else {
          saved = await api.createForm(postData);
        }
        this.$emit("success", saved);
      } catch (err) {
        this.$emit("error", "Eroare la salvarea formularului");
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.dialog = false;
      this.$emit("close");
    },
  },
  computed: {
    ...mapGetters(["getUser"]),
    grandTotal() {
      let total = 0;
      for (const item of this.form.items) {
        total += Number(item.pret || 0) * (item.cantitatea || 1);
      }
      return total;
    },
    isEdit() {
      return !!this.editData && !this.copy;
    },
    persoanaNames() {
      return this.persoane.map(p => p.name);
    },
    articleNames() {
      return this.articles.map(a => a.denumire);
    },
    templateOptions() {
      return this.templates.map(t => t.template_text);
    },
  },
  async mounted() {
    try {
      const [cats, allDepts, tmpls] = await Promise.all([
        api.getCategories(),
        equipmentApi.getDepartments(),
        api.getTemplates(),
      ]);
      this.categories = cats;
      // departments hidden in Configurare are not offered for new forms
      this.departments = allDepts.filter(dept => !dept.is_hidden).map(dept => dept.name);
      this.templates = tmpls;
    } catch (err) {
      this.$emit("error", "Eroare la încărcarea datelor");
    }
    // suggestions only help: the form works without them
    api.getSuggestions().then(({ persoane, articles }) => {
      this.persoane = persoane;
      this.articles = articles;
    }).catch(() => {});

    if (this.editData) {
      // Pre-fill from existing form
      this.form.department = this.editData.department || "";
      this.form.threshold_value = this.editData.threshold_value || 12000;
      this.form.notes = this.editData.notes || "";
      this.form.items = (this.editData.items || []).map(item => ({
        category_id: item.category_id,
        codul: item.codul || "",
        denumire: item.denumire || "",
        cantitatea: item.cantitatea || 1,
        pret: item.pret != null ? Number(item.pret) : null,
        anul_folosirii: item.anul_folosirii || 0,
        lunii: item.lunii || 0,
        anul_fabricatie: item.anul_fabricatie || null,
        data_punerii: item.data_punerii ? item.data_punerii.substring(0, 10) : null,
        defect_description: item.defect_description || "",
        persoana: item.persoana || "",
        // a copy describes other assets: inventory numbers are never copied
        inventoryNumbers: !this.copy && item.inventoryNumbers && item.inventoryNumbers.length > 0
          ? item.inventoryNumbers.map(inv => inv.nr_inventar || inv)
          : Array.from({ length: item.cantitatea || 1 }, () => ""),
        autoFilled: false,
      }));
    } else {
      // no default: the engineer's own department (DTI) used to be prefilled and stayed on other departments' forms
      this.form.items = [this.createEmptyItem()];
    }
  },
};
</script>

<style scoped>
.decontare-form-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.form-header {
  background-color: #2d8659;
  color: white;
  padding: 16px 24px;
}

.form-body {
  max-height: 72vh;
  overflow-y: auto;
  padding: 24px 28px !important;
  background-color: #fafafa;
}

.section-label {
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #2d8659;
  margin-bottom: 12px;
}

.section-card {
  background: white;
  border-radius: 8px;
  padding: 20px 16px 4px 16px;
  border: 1px solid #e0e0e0;
}

.item-card {
  background: white;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  transition: box-shadow 0.2s;
}
.item-card:hover {
  box-shadow: 0 2px 12px rgba(45, 134, 89, 0.12);
}

.item-header {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: linear-gradient(135deg, #f8fdf9 0%, #f0f7f2 100%);
  border-bottom: 1px solid #e8f0eb;
}

.item-number {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: #2d8659;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  margin-right: 10px;
  flex-shrink: 0;
}

.item-body {
  padding: 16px 16px 8px 16px;
}

.subsection {
  margin-top: 8px;
  padding: 12px 14px;
  background: #fafcfb;
  border-radius: 8px;
  border: 1px dashed #d0ddd5;
  margin-bottom: 8px;
}

.threshold-section {
  border-color: #ffb74d;
  background: #fff8f0;
}

.subsection-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 13px;
}

.form-footer {
  padding: 12px 24px;
  background: white;
}

.summary-text {
  font-size: 13px;
  color: #616161;
}
</style>
